/**
 * Net Income Detail Section
 * Extracted from FinancePage.jsx
 */

import React, { useState } from 'react';
import { ArrowLeft, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, Minus, Target, CheckCircle, AlertTriangle, Info, BarChart3, Landmark, Receipt } from 'lucide-react';
import {
  ComposedChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell, LabelList
} from 'recharts';
import { mockData } from '../../data/mockData';
import ExecutiveSummarySection from '../../components/ExecutiveSummarySection';
import ExecutiveDrilldownCard from '../../components/ExecutiveDrilldownCard';
import { 
  getCurrentPeriodLabel, 
  getPreviousPeriodLabel, 
  getBudgetLabel,
  getTitleSuffix,
  getComparisonSubtitle
} from '../../utils/timeLabels';

// ============ PREMIUM DESIGN TOKENS ============
const colors = {
  bg: {
    page: '#F5F7FB',
    surface: '#FFFFFF',
    header: '#0D1538',
    muted: '#F8FAFC',
    surfaceAlt: '#F8FAFC',
  },
  border: {
    default: '#E3E8F1',
    soft: '#EEF2F7',
  },
  text: {
    primary: '#0E1633',
    secondary: '#425070',
    tertiary: '#7A859D',
    inverse: '#FFFFFF',
  },
  status: {
    positive: '#0C8A4B',
    positiveBg: '#EAF7EF',
    warning: '#A07A18',
    warningBg: '#F8F1DD',
    negative: '#C33D52',
    negativeBg: '#FBECEF',
  },
  chart: {
    primary: '#0D1538',
    budget: '#94A3B8',
    prior: '#CBD5E1',
    positive: '#0C8A4B',
    negative: '#C33D52',
  },
  accent: {
    gold: '#C5A059',
    goldLight: '#E8D9B8',
  },
};

const formatM = (value) => {
  if (value === null || value === undefined) return '-';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return value;
  return num.toFixed(1);
};

// Compact KPI Tooltip - Standard Format
const CompactKPITooltip = ({ isVisible, title, definition, relevance, currentReading }) => {
  if (!isVisible) return null;
  return (
    <div 
      className="absolute z-50 w-56 bg-[#0A192F] border border-slate-700/50 rounded-lg shadow-xl p-3 bottom-full mb-2 left-1/2 -translate-x-1/2"
      style={{ boxShadow: '0 10px 25px rgba(10, 25, 47, 0.35)' }}
    >
      <div className="absolute left-1/2 -translate-x-1/2 w-2 h-2 bg-[#0A192F] border-slate-700/50 transform rotate-45 -bottom-1 border-b border-r" />
      <div className="space-y-2">
        <div className="flex items-center gap-1.5 pb-1.5 border-b border-slate-700/40">
          <div className="w-0.5 h-3 bg-gold-500 rounded-full"></div>
          <h4 className="text-gold-400 font-semibold text-xs uppercase tracking-wider">{title}</h4>
        </div>
        <div>
          <p className="text-slate-500 text-[10px] font-semibold uppercase tracking-wider mb-0.5">Definicao</p>
          <p className="text-white text-[11px] leading-snug">{definition}</p>
        </div>
        <div>
          <p className="text-slate-500 text-[10px] font-semibold uppercase tracking-wider mb-0.5">Relevancia</p>
          <p className="text-slate-300 text-[11px] leading-snug">{relevance}</p>
        </div>
        <div className="bg-slate-800/40 rounded px-2 py-1.5 border border-slate-700/20">
          <p className="text-slate-500 text-[10px] font-semibold uppercase tracking-wider mb-0.5">Leitura Actual</p>
          <p className="text-emerald-400 text-[11px] leading-snug">{currentReading}</p>
        </div>
      </div>
    </div>
  );
};

// KPI Card with Compact Tooltip and Trend Indicator
const KPICard = ({ title, value, unit, vsBudget, vsBudgetUnit = '%', yoyChange, yoyUnit = '%', color = 'primary', tooltipData, inverse = false }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  
  const isBudgetPositive = vsBudgetUnit === 'p.p.' ? parseFloat(vsBudget) >= 0 : parseFloat(vsBudget) >= 0;
  const isYoyPositive = yoyUnit === 'p.p.' ? parseFloat(yoyChange) >= 0 : parseFloat(yoyChange) >= 0;
  
  // Determine trend indicator
  const numYoY = parseFloat(yoyChange) || 0;
  const isPositiveOutcome = inverse ? numYoY < 0 : numYoY > 0;
  const trendBg = isPositiveOutcome ? 'bg-emerald-50' : 'bg-red-50';
  const trendColor = isPositiveOutcome ? '#10B981' : '#EF4444';
  const TrendIcon = numYoY > 0 ? ArrowUpRight : numYoY < 0 ? ArrowDownRight : Minus;
  
  return (
    <div 
      className={`bg-white rounded-xl border border-slate-200 p-5 hover:shadow-lg transition-all duration-300 relative overflow-visible ${tooltipData ? 'cursor-help' : ''}`}
      onMouseEnter={() => tooltipData && setShowTooltip(true)}
      onMouseLeave={() => tooltipData && setShowTooltip(false)}
    >
      {/* Trend Arrow Indicator */}
      <div 
        className={`absolute top-4 right-4 w-7 h-7 rounded-full flex items-center justify-center ${trendBg}`}
      >
        <TrendIcon className="w-4 h-4" style={{ color: trendColor }} />
      </div>
      
      {/* Compact Tooltip */}
      {tooltipData && (
        <CompactKPITooltip 
          isVisible={showTooltip}
          title={tooltipData.title}
          definition={tooltipData.definition}
          relevance={tooltipData.relevance}
          currentReading={tooltipData.currentReading}
        />
      )}
      
      <p className="text-xs text-slate-500 tracking-wider mb-1">{title}</p>
      <p className={`font-serif text-3xl font-semibold ${color === 'gold' ? 'text-gold-600' : 'text-primary'}`}>
        {value}<span className="text-base text-slate-400 ml-1">{unit}</span>
      </p>
      <div className="flex flex-wrap items-center gap-1.5 mt-3">
        <span className={`text-xs font-semibold px-1.5 py-0.5 rounded flex items-center gap-1 ${isBudgetPositive ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
          {parseFloat(vsBudget) >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          {parseFloat(vsBudget) >= 0 ? '+' : ''}{vsBudget}{vsBudgetUnit} vs Orç.
        </span>
        <span className={`text-xs font-semibold px-1.5 py-0.5 rounded flex items-center gap-1 ${isYoyPositive ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
          {parseFloat(yoyChange) >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          {parseFloat(yoyChange) >= 0 ? '+' : ''}{yoyChange}{yoyUnit} YoY
        </span>
      </div>
    </div>
  );
};

const NetIncomeDetail = ({ year, language, setActiveSection, flowMap }) => {
  const netIncomeData = mockData.netIncomeDetails[year];
  const prevNetIncomeData = mockData.netIncomeDetails[year - 1] || mockData.netIncomeDetails[2024];
  const budgetData = mockData.finance.realVsBudget[year] || mockData.finance.realVsBudget[2025];
  
  // Dynamic time labels based on year (quarterly format for 2026)
  const currentPeriod = getCurrentPeriodLabel(year, language);
  const previousPeriod = getPreviousPeriodLabel(year, language);
  const budgetLabelFull = getBudgetLabel(year, language);
  const titleSuffix = getTitleSuffix(year, language);
  const comparisonSubtitle = getComparisonSubtitle(year, language);
  
  // Budget estimates for each KPI
  const budgetNetIncome = budgetData.netIncome?.budget || netIncomeData.netIncome * 0.95;
  const netIncomeVsBudget = (netIncomeData.netIncome - budgetNetIncome) / budgetNetIncome * 100;
  const netIncomeVsPrior = (netIncomeData.netIncome / prevNetIncomeData.netIncome - 1) * 100;
  const netMargin = netIncomeData.netMargin;
  
  const budgetMargin = prevNetIncomeData.netMargin + 1.5; // Budget margin estimate
  const marginVsBudget = (netIncomeData.netMargin - budgetMargin).toFixed(1);
  
  const budgetEbit = netIncomeData.ebit * 0.96;
  const ebitVsBudget = (netIncomeData.ebit - budgetEbit) / budgetEbit * 100;
  
  const budgetRoe = 29.5;
  const roeVsBudget = ((netIncomeData.roe || 31) - budgetRoe).toFixed(1);

  // Waterfall chart data - Classic waterfall with running totals
  let runningTotal = 0;
  
  const waterfallData = [
    { 
      displayName: "EBITDA", 
      start: 0,
      end: netIncomeData.ebitda,
      value: netIncomeData.ebitda,
      type: "start"
    },
    { 
      displayName: language === 'pt' ? "Amortizações" : "Amortization", 
      start: netIncomeData.ebitda,
      end: netIncomeData.ebitda + netIncomeData.depreciation,
      value: netIncomeData.depreciation,
      type: "cost"
    },
    { 
      displayName: "EBIT", 
      start: 0,
      end: netIncomeData.ebit,
      value: netIncomeData.ebit,
      type: "subtotal"
    },
    { 
      displayName: language === 'pt' ? "Resultados Financeiros" : "Financial Results", 
      start: netIncomeData.ebit,
      end: netIncomeData.ebit + netIncomeData.financialResult,
      value: netIncomeData.financialResult,
      type: "cost"
    },
    { 
      displayName: language === 'pt' ? "Resultados Não Operacionais" : "Non-Operating Results", 
      start: netIncomeData.ebit + netIncomeData.financialResult,
      end: netIncomeData.ebit + netIncomeData.financialResult + netIncomeData.nonOperatingResult,
      value: netIncomeData.nonOperatingResult,
      type: netIncomeData.nonOperatingResult >= 0 ? "income" : "cost"
    },
    { 
      displayName: language === 'pt' ? "EBT" : "EBT", 
      start: 0,
      end: netIncomeData.ebt,
      value: netIncomeData.ebt,
      type: "subtotal"
    },
    { 
      displayName: language === 'pt' ? "Imposto Industrial" : "Industrial Tax", 
      start: netIncomeData.ebt,
      end: netIncomeData.ebt + netIncomeData.taxes,
      value: netIncomeData.taxes,
      type: "cost"
    },
    { 
      displayName: language === 'pt' ? "Res. Líquido" : "Net Income", 
      start: 0,
      end: netIncomeData.netIncome,
      value: netIncomeData.netIncome,
      type: "total"
    },
  ].map(item => ({
    ...item,
    // For waterfall: invisible bar to position + visible bar for value
    invisible: item.type === 'start' || item.type === 'subtotal' || item.type === 'total' 
      ? 0 
      : Math.min(item.start, item.end),
    barValue: item.type === 'start' || item.type === 'subtotal' || item.type === 'total'
      ? item.end
      : Math.abs(item.value),
  }));

  // Executive Summary insights
  const executiveSummary = language === 'pt' ? [
    `O Resultado Líquido de ${formatM(netIncomeData.netIncome)}M USD representa um crescimento de +${Math.round((netIncomeData.netIncome / prevNetIncomeData.netIncome - 1) * 100)}% face ao período homólogo e ${netIncomeVsBudget >= 0 ? '+' : ''}${netIncomeVsBudget.toFixed(1)}% Orçamento 2025.`,
    `A margem líquida expandiu de ${prevNetIncomeData.netMargin}% para ${netIncomeData.netMargin}% (+${(netIncomeData.netMargin - prevNetIncomeData.netMargin).toFixed(1)}p.p.), reflectindo a melhoria da eficiência operacional.`,
    `Os encargos financeiros de ${formatM(Math.abs(netIncomeData.financialResult))}M USD representam uma redução face ao período anterior, beneficiando do contexto de taxas de juro mais baixas.`
  ] : [
    `Net Income of ${formatM(netIncomeData.netIncome)}M USD represents a growth of +${Math.round((netIncomeData.netIncome / prevNetIncomeData.netIncome - 1) * 100)}% vs prior year and ${netIncomeVsBudget >= 0 ? '+' : ''}${netIncomeVsBudget.toFixed(1)}% Budget 2025.`,
    `Net margin expanded from ${prevNetIncomeData.netMargin}% to ${netIncomeData.netMargin}% (+${(netIncomeData.netMargin - prevNetIncomeData.netMargin).toFixed(1)}p.p.), reflecting improved operational efficiency.`,
    `Financial charges of ${formatM(Math.abs(netIncomeData.financialResult))}M USD represent a reduction vs prior period, benefiting from lower interest rate environment.`
  ];

  const waterfallColors = {
    start: '#0A192F',
    income: '#059669',
    cost: '#DC2626',
    subtotal: '#64748B',
    total: '#0A192F',
  };

  // Table colors
  const tableColors = {
    bg: { header: '#0A192F' },
    status: {
      positive: '#059669',
      negative: '#DC2626',
      positiveBg: '#D1FAE5',
      negativeBg: '#FEE2E2'
    }
  };

  // Income Statement from EBITDA to Net Income table data
  const incomeStatementData = [
    { 
      label: { pt: 'EBITDA', en: 'EBITDA' }, 
      actual: netIncomeData.ebitda, 
      budget: netIncomeData.ebitda * 0.96, 
      prior: prevNetIncomeData.ebitda,
      type: 'highlight',
      isNegative: false
    },
    { 
      label: { pt: 'Margem EBITDA', en: 'EBITDA Margin' }, 
      actual: (netIncomeData.ebitda / 298.2 * 100), 
      budget: (netIncomeData.ebitda * 0.96 / 285.0 * 100), 
      prior: (prevNetIncomeData.ebitda / 246.4 * 100),
      type: 'margin',
      isPercent: true,
      isNegative: false
    },
    { 
      label: { pt: 'Amortizações', en: 'Amortization' }, 
      actual: netIncomeData.depreciation, 
      budget: netIncomeData.depreciation * 0.95, 
      prior: prevNetIncomeData.depreciation || -17.2,
      type: 'row',
      isNegative: true
    },
    { 
      label: { pt: 'EBIT', en: 'EBIT' }, 
      actual: netIncomeData.ebit, 
      budget: netIncomeData.ebit * 0.96, 
      prior: prevNetIncomeData.ebit,
      type: 'subtotal',
      isNegative: false
    },
    { 
      label: { pt: 'Margem EBIT', en: 'EBIT Margin' }, 
      actual: (netIncomeData.ebit / 298.2 * 100), 
      budget: (netIncomeData.ebit * 0.96 / 285.0 * 100), 
      prior: (prevNetIncomeData.ebit / 246.4 * 100),
      type: 'margin',
      isPercent: true,
      isNegative: false
    },
    { 
      label: { pt: 'Resultados Financeiros', en: 'Financial Results' }, 
      actual: netIncomeData.financialResult, 
      budget: netIncomeData.financialResult * 0.9, 
      prior: prevNetIncomeData.financialResult || -5.5,
      type: 'row',
      isNegative: true
    },
    { 
      label: { pt: 'Resultados Não Operacionais', en: 'Non-Operating Results' }, 
      actual: netIncomeData.nonOperatingResult, 
      budget: netIncomeData.nonOperatingResult * 0.8, 
      prior: prevNetIncomeData.nonOperatingResult || 0.8,
      type: 'row',
      isNegative: false
    },
    { 
      label: { pt: 'EBT (Resultado Antes de Impostos)', en: 'EBT (Earnings Before Taxes)' }, 
      actual: netIncomeData.ebt, 
      budget: netIncomeData.ebt * 0.96, 
      prior: prevNetIncomeData.ebt,
      type: 'subtotal',
      isNegative: false
    },
    { 
      label: { pt: 'Imposto Industrial', en: 'Industrial Tax' }, 
      actual: netIncomeData.taxes, 
      budget: netIncomeData.taxes * 0.95, 
      prior: prevNetIncomeData.taxes || -20.8,
      type: 'row',
      isNegative: true
    },
    { 
      label: { pt: 'Resultado Líquido', en: 'Net Income' }, 
      actual: netIncomeData.netIncome, 
      budget: budgetNetIncome, 
      prior: prevNetIncomeData.netIncome,
      type: 'highlight',
      isNegative: false
    },
    { 
      label: { pt: 'Margem Líquida', en: 'Net Margin' }, 
      actual: netIncomeData.netMargin, 
      budget: budgetMargin, 
      prior: prevNetIncomeData.netMargin,
      type: 'margin',
      isPercent: true,
      isNegative: false
    },
  ];

  return (
    <div className="space-y-6" data-testid="netincome-detail-section">
      {/* Header */}
      <div>
        <button 
          onClick={() => setActiveSection('income-statement')}
          className="flex items-center gap-2 text-sm mb-4 transition-colors hover:opacity-80"
          style={{ color: colors.text.tertiary }}
        >
          <ArrowLeft className="w-4 h-4" />
          {language === 'pt' ? 'Voltar à Demonstração de Resultados' : 'Back to Income Statement'}
        </button>
        <div className="flex items-start justify-between">
          <div>
            <h1 
              className="text-[38px] leading-[44px] font-semibold tracking-[-0.3px] mb-2"
              style={{ color: colors.text.primary, fontFamily: '"Source Serif 4", serif' }}
            >
              {language === 'pt' ? 'Resultado Líquido' : 'Net Income'} | {titleSuffix}
            </h1>
            <p 
              className="text-[15px] leading-[22px]"
              style={{ color: colors.text.secondary }}
            >
              {comparisonSubtitle}
            </p>
          </div>
          
        </div>
        {/* Finance Flow Map - appears below title, above key messages */}
        {flowMap && <div className="mt-4">{flowMap}</div>}
      </div>

      {/* ============ KEY MESSAGES BAND - CAPEX Style ============ */}
      <div className="bg-gradient-to-r from-[#0A192F] to-[#1a2744] rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-1 h-5 bg-gold-500 rounded-full"></div>
          <h3 className="text-gold-400 font-semibold text-sm uppercase tracking-wider">
            {language === 'pt' ? 'Mensagens-Chave' : 'Key Messages'}
          </h3>
        </div>
        <div className="grid grid-cols-3 gap-5">
          {/* Resultado */}
          <div className="bg-slate-700/30 border border-slate-600/30 rounded-xl p-4 transition-all duration-300 hover:scale-[1.02]">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-amber-500/30">
                <Target className="w-5 h-5 text-amber-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-sm mb-1.5 leading-tight text-gold-400">
                  {language === 'pt' ? 'RESULTADO' : 'RESULT'}
                </h4>
                <p className="text-slate-400 text-xs leading-relaxed">
                  {language === 'pt' 
                    ? `Resultado Liquido de ${formatM(netIncomeData.netIncome)}M USD, ${netIncomeVsBudget >= 0 ? '+' : ''}${netIncomeVsBudget.toFixed(0)}% Orcamento e +${Math.round(netIncomeVsPrior)}% YoY, superando expectativas.`
                    : `Net Income of ${formatM(netIncomeData.netIncome)}M USD, ${netIncomeVsBudget >= 0 ? '+' : ''}${netIncomeVsBudget.toFixed(0)}% Budget and +${Math.round(netIncomeVsPrior)}% YoY, exceeding expectations.`
                  }
                </p>
              </div>
            </div>
          </div>
          
          {/* Margem */}
          <div className="bg-slate-700/30 border border-slate-600/30 rounded-xl p-4 transition-all duration-300 hover:scale-[1.02]">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-emerald-500/30">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-sm mb-1.5 leading-tight text-emerald-400">
                  {language === 'pt' ? 'MARGEM' : 'MARGIN'}
                </h4>
                <p className="text-slate-400 text-xs leading-relaxed">
                  {language === 'pt' 
                    ? `Margem liquida expandiu de ${prevNetIncomeData.netMargin}% para ${netIncomeData.netMargin}% (+${(netIncomeData.netMargin - prevNetIncomeData.netMargin).toFixed(1)}p.p.), reflectindo eficiencia operacional.`
                    : `Net margin expanded from ${prevNetIncomeData.netMargin}% to ${netIncomeData.netMargin}% (+${(netIncomeData.netMargin - prevNetIncomeData.netMargin).toFixed(1)}p.p.), reflecting operational efficiency.`
                  }
                </p>
              </div>
            </div>
          </div>
          
          {/* Drivers */}
          <div className="bg-slate-700/30 border border-slate-600/30 rounded-xl p-4 transition-all duration-300 hover:scale-[1.02]">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-blue-500/30">
                <AlertTriangle className="w-5 h-5 text-blue-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-sm mb-1.5 leading-tight text-blue-400">
                  {language === 'pt' ? 'DRIVERS' : 'DRIVERS'}
                </h4>
                <p className="text-slate-400 text-xs leading-relaxed">
                  {language === 'pt' 
                    ? `Encargos financeiros de ${formatM(Math.abs(netIncomeData.financialResult))}M USD reduzidos face ao periodo anterior, beneficiando de taxas de juro mais baixas.`
                    : `Financial charges of ${formatM(Math.abs(netIncomeData.financialResult))}M USD reduced vs prior period, benefiting from lower interest rates.`
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* KPIs with Budget Comparison */}
      <div className="grid grid-cols-4 gap-4">
        <KPICard 
          title={language === 'pt' ? 'Resultado Líquido' : 'Net Income'}
          value={formatM(netIncomeData.netIncome)}
          unit="M USD"
          vsBudget={netIncomeVsBudget.toFixed(1)}
          yoyChange={Math.round((netIncomeData.netIncome / prevNetIncomeData.netIncome - 1) * 100)}
          tooltipData={{
            title: language === 'pt' ? 'Resultado Líquido' : 'Net Income',
            definition: language === 'pt' 
              ? 'Lucro final após todos os custos, impostos e itens não operacionais.'
              : 'Final profit after all costs, taxes and non-operating items.',
            relevance: language === 'pt'
              ? 'Montante disponível para distribuição aos acionistas ou reinvestimento.'
              : 'Amount available for shareholder distribution or reinvestment.',
            currentReading: language === 'pt'
              ? `${formatM(netIncomeData.netIncome)}M USD, ${netIncomeVsBudget >= 0 ? '+' : ''}${netIncomeVsBudget.toFixed(1)}% Orçamento`
              : `${formatM(netIncomeData.netIncome)}M USD, ${netIncomeVsBudget >= 0 ? '+' : ''}${netIncomeVsBudget.toFixed(1)}% Budget`
          }}
        />
        <KPICard 
          title={language === 'pt' ? 'Margem Líquida' : 'Net Margin'}
          value={netIncomeData.netMargin}
          unit="%"
          vsBudget={marginVsBudget}
          vsBudgetUnit="p.p."
          yoyChange={(netIncomeData.netMargin - prevNetIncomeData.netMargin).toFixed(1)}
          yoyUnit="p.p."
          color="gold"
          tooltipData={{
            title: language === 'pt' ? 'Margem Líquida' : 'Net Margin',
            definition: language === 'pt' 
              ? 'Percentagem da receita que se converte em lucro líquido.'
              : 'Percentage of revenue converted to net profit.',
            relevance: language === 'pt'
              ? 'Mede a eficiência total do negócio em gerar valor para o acionista.'
              : 'Measures total business efficiency in generating shareholder value.',
            currentReading: language === 'pt'
              ? `${netIncomeData.netMargin}%, ${marginVsBudget >= 0 ? '+' : ''}${marginVsBudget}p.p. vs Orçamento`
              : `${netIncomeData.netMargin}%, ${marginVsBudget >= 0 ? '+' : ''}${marginVsBudget}p.p. vs Budget`
          }}
        />
        <KPICard 
          title="EBIT"
          value={formatM(netIncomeData.ebit)}
          unit="M USD"
          vsBudget={ebitVsBudget.toFixed(1)}
          yoyChange={Math.round((netIncomeData.ebit / prevNetIncomeData.ebit - 1) * 100)}
          tooltipData={{
            title: 'EBIT',
            definition: language === 'pt' 
              ? 'Resultado operacional antes de juros e impostos.'
              : 'Operating result before interest and taxes.',
            relevance: language === 'pt'
              ? 'Lucro gerado exclusivamente pela actividade operacional, excluindo estrutura financeira.'
              : 'Profit generated exclusively by operating activity, excluding financial structure.',
            currentReading: language === 'pt'
              ? `${formatM(netIncomeData.ebit)}M USD, ${ebitVsBudget >= 0 ? '+' : ''}${ebitVsBudget.toFixed(1)}% Orçamento`
              : `${formatM(netIncomeData.ebit)}M USD, ${ebitVsBudget >= 0 ? '+' : ''}${ebitVsBudget.toFixed(1)}% Budget`
          }}
        />
        <KPICard 
          title="ROE"
          value={netIncomeData.roe || 31}
          unit="%"
          vsBudget={roeVsBudget}
          vsBudgetUnit="p.p."
          yoyChange="2.3"
          yoyUnit="p.p."
          tooltipData={{
            title: 'ROE',
            definition: language === 'pt' 
              ? 'Retorno sobre o capital próprio: resultado líquido dividido pelo património.'
              : 'Return on equity: net income divided by shareholders\' equity.',
            relevance: language === 'pt'
              ? 'Mede a rentabilidade do investimento dos acionistas.'
              : 'Measures return on shareholders\' investment.',
            currentReading: language === 'pt'
              ? `${netIncomeData.roe || 31}%, ${roeVsBudget >= 0 ? '+' : ''}${roeVsBudget}p.p. vs Orçamento`
              : `${netIncomeData.roe || 31}%, ${roeVsBudget >= 0 ? '+' : ''}${roeVsBudget}p.p. vs Budget`
          }}
        />
      </div>

      {/* ============ EXECUTIVE SUMMARY - EBITDA Style ============ */}
      <ExecutiveSummarySection
        title={language === 'pt' ? 'Sumário Executivo' : 'Executive Summary'}
        subtitle={language === 'pt' ? 'Resultado líquido e rentabilidade' : 'Net income and profitability'}
        language={language}
        maxHeight="500px"
        icon={Info}
        dataTestId="net-income-executive-summary"
      >
        {/* Executive Overview */}
        <div className="mb-6">
          <p className="text-slate-700 leading-relaxed text-sm mb-4">
            {language === 'pt' 
              ? `Em ${year}, o Resultado Líquido atingiu USD ${formatM(netIncomeData.netIncome)}M, ficando ${netIncomeVsBudget >= 0 ? netIncomeVsBudget.toFixed(1) + '% acima' : Math.abs(netIncomeVsBudget).toFixed(1) + '% abaixo'} do Orçamento ${year} e ${netIncomeVsPrior >= 0 ? netIncomeVsPrior.toFixed(1) + '% acima' : Math.abs(netIncomeVsPrior).toFixed(1) + '% abaixo'} de ${year - 1}. A margem líquida subiu para ${netMargin.toFixed(1)}%, acima dos ${(netMargin - 1.2).toFixed(1)}% orçamentados e dos ${(netMargin - 2.7).toFixed(1)}% registados em ${year - 1}, o que mostra que a melhoria do resultado não resultou apenas de maior escala, mas também de melhor retenção de resultado ao longo da Demonstração de Resultados.`
              : `In ${year}, Net Income reached USD ${formatM(netIncomeData.netIncome)}M, ${netIncomeVsBudget >= 0 ? netIncomeVsBudget.toFixed(1) + '% above' : Math.abs(netIncomeVsBudget).toFixed(1) + '% below'} ${year} Budget and ${netIncomeVsPrior >= 0 ? netIncomeVsPrior.toFixed(1) + '% above' : Math.abs(netIncomeVsPrior).toFixed(1) + '% below'} ${year - 1}. Net margin rose to ${netMargin.toFixed(1)}%, above the budgeted ${(netMargin - 1.2).toFixed(1)}% and ${year - 1}'s ${(netMargin - 2.7).toFixed(1)}%, showing that the result improvement came not only from greater scale, but also from better result retention throughout the Income Statement.`}
          </p>
        </div>

        {/* Driver Sections - Collapsible Cards */}
        <div className="space-y-4">
          {/* Resultado Operacional */}
          <div className="bg-gradient-to-r from-blue-50 to-slate-50 rounded-xl p-5 border border-blue-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Resultado Operacional' : 'Operating Result'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? 'Principal driver do resultado líquido' : 'Main driver of net income'}</p>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? `A principal explicação para esta evolução esteve no nível de resultado operacional. O EBIT fechou em USD ${formatM(netIncomeData.ebit)}M, acima do orçamento em ${ebitVsBudget.toFixed(1)}% e acima de ${year - 1} em ${((netIncomeData.ebit / prevNetIncomeData.ebit - 1) * 100).toFixed(1)}%, sustentado por um EBITDA de USD ${formatM(netIncomeData.ebitda)}M, também ${((netIncomeData.ebitda / (netIncomeData.ebitda * 0.96) - 1) * 100).toFixed(1)}% acima do plano e ${((netIncomeData.ebitda / prevNetIncomeData.ebitda - 1) * 100).toFixed(1)}% acima do período homólogo.`
                : `The main explanation for this evolution was the level of operating result. EBIT closed at USD ${formatM(netIncomeData.ebit)}M, ${ebitVsBudget.toFixed(1)}% above budget and ${((netIncomeData.ebit / prevNetIncomeData.ebit - 1) * 100).toFixed(1)}% above ${year - 1}, sustained by EBITDA of USD ${formatM(netIncomeData.ebitda)}M, also ${((netIncomeData.ebitda / (netIncomeData.ebitda * 0.96) - 1) * 100).toFixed(1)}% above plan and ${((netIncomeData.ebitda / prevNetIncomeData.ebitda - 1) * 100).toFixed(1)}% above prior year.`}
            </p>
            <div className="bg-white/60 rounded-lg p-3 border border-blue-100">
              <p className="text-xs font-semibold text-slate-700 mb-2">{language === 'pt' ? 'Indicadores Operacionais' : 'Operating Indicators'}</p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                  {language === 'pt' ? `EBITDA: ${formatM(netIncomeData.ebitda)}M USD (+${((netIncomeData.ebitda / (netIncomeData.ebitda * 0.96) - 1) * 100).toFixed(1)}% Orç.)` : `EBITDA: ${formatM(netIncomeData.ebitda)}M USD (+${((netIncomeData.ebitda / (netIncomeData.ebitda * 0.96) - 1) * 100).toFixed(1)}% Budget)`}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                  {language === 'pt' ? `EBIT: ${formatM(netIncomeData.ebit)}M USD (+${ebitVsBudget.toFixed(1)}% Orç.)` : `EBIT: ${formatM(netIncomeData.ebit)}M USD (+${ebitVsBudget.toFixed(1)}% Budget)`}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                  {language === 'pt' ? `Margem EBIT: ${(netIncomeData.ebit / 298.2 * 100).toFixed(1)}%` : `EBIT Margin: ${(netIncomeData.ebit / 298.2 * 100).toFixed(1)}%`}
                </li>
              </ul>
            </div>
          </div>

          {/* Rubricas Abaixo do EBIT */}
          <div className="bg-gradient-to-r from-amber-50 to-slate-50 rounded-xl p-5 border border-amber-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <TrendingDown className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Rubricas Abaixo do EBIT' : 'Items Below EBIT'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? 'Encargos financeiros e impostos' : 'Financial charges and taxes'}</p>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? `Abaixo do EBIT, a leitura é mista: os resultados financeiros melhoraram face a ${year - 1}, passando de -USD ${Math.abs(prevNetIncomeData.financialResult).toFixed(1)}M para -USD ${Math.abs(netIncomeData.financialResult).toFixed(1)}M, mas ficaram USD 0,6M abaixo do orçamento; já os resultados não operacionais ficaram em -USD ${Math.abs(netIncomeData.nonOperatingResult).toFixed(1)}M, piores do que o orçamento e ligeiramente abaixo de ${year - 1}. Ainda assim, o EBT atingiu USD ${formatM(netIncomeData.ebt)}M, superando o orçamento em ${((netIncomeData.ebt / (netIncomeData.ebt * 0.96) - 1) * 100).toFixed(1)}% e ficando ${((netIncomeData.ebt / prevNetIncomeData.ebt - 1) * 100).toFixed(1)}% acima de ${year - 1}.`
                : `Below EBIT, the reading is mixed: financial results improved vs ${year - 1}, from -USD ${Math.abs(prevNetIncomeData.financialResult).toFixed(1)}M to -USD ${Math.abs(netIncomeData.financialResult).toFixed(1)}M, but were USD 0.6M below budget; non-operating results were -USD ${Math.abs(netIncomeData.nonOperatingResult).toFixed(1)}M, worse than budget and slightly below ${year - 1}. Still, EBT reached USD ${formatM(netIncomeData.ebt)}M, exceeding budget by ${((netIncomeData.ebt / (netIncomeData.ebt * 0.96) - 1) * 100).toFixed(1)}% and ${((netIncomeData.ebt / prevNetIncomeData.ebt - 1) * 100).toFixed(1)}% above ${year - 1}.`}
            </p>
            <p className="text-slate-600 text-xs leading-relaxed">
              {language === 'pt' 
                ? `A maior dedução abaixo do EBT continuou a ser o Imposto Industrial, que totalizou -USD ${Math.abs(netIncomeData.taxes).toFixed(1)}M, acima dos -USD ${(Math.abs(netIncomeData.taxes) * 0.95).toFixed(1)}M orçamentados e dos -USD ${Math.abs(prevNetIncomeData.taxes).toFixed(1)}M em ${year - 1}. Isto significa que a melhoria do resultado líquido foi suficientemente forte para absorver maior carga fiscal e ainda assim fechar o ano acima do plano.`
                : `The largest deduction below EBT continued to be Industrial Tax, totaling -USD ${Math.abs(netIncomeData.taxes).toFixed(1)}M, above the budgeted -USD ${(Math.abs(netIncomeData.taxes) * 0.95).toFixed(1)}M and ${year - 1}'s -USD ${Math.abs(prevNetIncomeData.taxes).toFixed(1)}M. This means the net income improvement was strong enough to absorb higher tax burden and still close the year above plan.`}
            </p>
          </div>

          {/* Síntese à Gerência */}
          <div className="bg-gradient-to-r from-emerald-50 to-slate-50 rounded-xl p-5 border border-emerald-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <ArrowUpRight className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Síntese à Gerência' : 'Management Synthesis'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? 'Leitura consolidada do período' : 'Consolidated reading of the period'}</p>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? `O Resultado Líquido de USD ${formatM(netIncomeData.netIncome)}M confirma um ano de melhoria efectiva da rentabilidade, com crescimento superior ao do orçamento e de ${year - 1}. O resultado foi sustentado sobretudo por maior geração operacional, reflectida no EBITDA e no EBIT, e não por factores extraordinários abaixo da linha.`
                : `Net Income of USD ${formatM(netIncomeData.netIncome)}M confirms a year of effective profitability improvement, with growth exceeding both budget and ${year - 1}. The result was sustained mainly by higher operational generation, reflected in EBITDA and EBIT, and not by extraordinary factors below the line.`}
            </p>
            <p className="text-slate-600 text-xs leading-relaxed">
              {language === 'pt' 
                ? 'A análise das páginas seguintes deverá concentrar-se em três pontos: qualidade da melhoria operacional, comportamento das rubricas abaixo do EBIT e capacidade de manter margem líquida acima do plano num contexto de maior carga fiscal.'
                : 'The analysis of the following pages should focus on three points: quality of operational improvement, behavior of items below EBIT, and ability to maintain net margin above plan in a context of higher tax burden.'}
            </p>
          </div>
        </div>

        {/* ============ DRILLDOWN NAVIGATION CARDS ============ */}
        {setActiveSection && (
          <div className="mt-6 pt-4 border-t border-slate-200">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
              {language === 'pt' ? 'Análises Relacionadas' : 'Related Analysis'}
            </p>
            <div className="grid grid-cols-3 gap-3">
              <ExecutiveDrilldownCard
                icon={BarChart3}
                title={language === 'pt' ? 'EBITDA' : 'EBITDA'}
                subtitle={language === 'pt' ? 'Geração operacional' : 'Operating generation'}
                navigateTo="ebitda-detail"
                setActiveSection={setActiveSection}
                color="blue"
                language={language}
              >
                <p>
                  {language === 'pt' 
                    ? 'Analise a margem operacional que sustenta o resultado.'
                    : 'Analyze the operating margin that sustains the result.'}
                </p>
              </ExecutiveDrilldownCard>

              <ExecutiveDrilldownCard
                icon={Landmark}
                title={language === 'pt' ? 'Resultados Financeiros' : 'Financial Results'}
                subtitle={language === 'pt' ? 'Custo do funding' : 'Funding cost'}
                navigateTo="financial-results"
                setActiveSection={setActiveSection}
                color="rose"
                language={language}
              >
                <p>
                  {language === 'pt' 
                    ? 'Veja o impacto dos custos financeiros no resultado líquido.'
                    : 'See financial costs impact on net income.'}
                </p>
              </ExecutiveDrilldownCard>

              <ExecutiveDrilldownCard
                icon={Receipt}
                title={language === 'pt' ? 'Imposto Industrial' : 'Industrial Tax'}
                subtitle={language === 'pt' ? 'Carga fiscal' : 'Tax burden'}
                navigateTo="industrial-tax"
                setActiveSection={setActiveSection}
                color="amber"
                language={language}
              >
                <p>
                  {language === 'pt' 
                    ? 'Analise o impacto fiscal sobre a rentabilidade final.'
                    : 'Analyze tax impact on final profitability.'}
                </p>
              </ExecutiveDrilldownCard>
            </div>
          </div>
        )}
      </ExecutiveSummarySection>

      {/* Waterfall Chart - Full Width */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="font-serif text-lg font-semibold text-primary mb-4">
          {language === 'pt' ? 'Do EBITDA ao Resultado Líquido' : 'From EBITDA to Net Income'}
        </h3>
        <ResponsiveContainer width="100%" height={350}>
          <ComposedChart data={waterfallData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
            <XAxis 
              dataKey="displayName" 
              tick={{ fontSize: 11, fill: '#64748B' }} 
              stroke="#94A3B8" 
              height={60}
              angle={-25}
              textAnchor="end"
            />
            <YAxis 
              tick={{ fontSize: 11, fill: '#64748B' }} 
              stroke="#94A3B8" 
              tickFormatter={(value) => `${value}M`}
              domain={[0, 'auto']}
            />
            <Tooltip 
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  return (
                    <div className="bg-primary text-white rounded-lg px-4 py-3 shadow-xl">
                      <p className="text-sm font-medium">{data.displayName}</p>
                      <p className="text-lg font-bold">{data.value >= 0 ? '' : ''}{data.value}M USD</p>
                    </div>
                  );
                }
                return null;
              }}
            />
            {/* Invisible bar for positioning */}
            <Bar dataKey="invisible" stackId="waterfall" fill="transparent" />
            {/* Visible bar with colors */}
            <Bar dataKey="barValue" stackId="waterfall" radius={[4, 4, 0, 0]}>
              {waterfallData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={waterfallColors[entry.type]} 
                />
              ))}
              <LabelList 
                dataKey="value" 
                position="top" 
                formatter={(value) => `${value >= 0 ? '' : ''}${value}M`}
                style={{ 
                  fontSize: '10px', 
                  fontWeight: '600',
                  fill: '#475569'
                }}
              />
            </Bar>
          </ComposedChart>
        </ResponsiveContainer>
        {/* Legend */}
        <div className="flex items-center justify-center gap-6 mt-4 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-[#0A192F]"></div>
            <span className="text-slate-500">{language === 'pt' ? 'Valores Totais' : 'Total Values'}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-[#059669]"></div>
            <span className="text-slate-500">{language === 'pt' ? 'Adições' : 'Additions'}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-[#DC2626]"></div>
            <span className="text-slate-500">{language === 'pt' ? 'Deduções' : 'Deductions'}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-[#64748B]"></div>
            <span className="text-slate-500">{language === 'pt' ? 'Subtotais' : 'Subtotals'}</span>
          </div>
        </div>
      </div>

      {/* Income Statement Table: EBITDA to Net Income */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100">
          <h3 className="font-serif text-lg font-semibold text-primary">
            {language === 'pt' ? 'Do EBITDA ao Resultado Líquido' : 'From EBITDA to Net Income'}
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ backgroundColor: colors.bg.header }}>
                <th className="text-left py-3 px-4 text-xs font-semibold text-white uppercase tracking-wider w-[22%]">
                  {language === 'pt' ? 'Descrição' : 'Description'}
                </th>
                <th className="text-right py-3 px-3 text-xs font-semibold text-white uppercase tracking-wider w-[11%]">
                  {year}
                </th>
                <th className="text-right py-3 px-3 text-xs font-semibold text-white uppercase tracking-wider w-[11%]">
                  {language === 'pt' ? 'Orçamento' : 'Budget'} {year}
                </th>
                <th className="text-right py-3 px-3 text-xs font-semibold text-amber-300 uppercase tracking-wider w-[11%]">
                  {language === 'pt' ? 'Var. Abs.' : 'Abs. Var.'}
                </th>
                <th className="text-right py-3 px-3 text-xs font-semibold text-amber-300 uppercase tracking-wider w-[11%]">
                  {language === 'pt' ? 'Var. %' : 'Var. %'}
                </th>
                <th className="text-right py-3 px-3 text-xs font-semibold text-slate-400 uppercase tracking-wider w-[11%]">
                  {year - 1}
                </th>
                <th className="text-right py-3 px-3 text-xs font-semibold text-slate-400 uppercase tracking-wider w-[11%]">
                  {language === 'pt' ? 'Var. % YoY' : 'Var. % YoY'}
                </th>
              </tr>
            </thead>
            <tbody>
              {incomeStatementData.map((row, idx) => {
                const absVarVsBudget = row.isPercent 
                  ? (row.actual - row.budget).toFixed(1)
                  : (row.actual - row.budget).toFixed(1);
                const pctVarVsBudget = row.budget !== 0 && !row.isPercent
                  ? ((row.actual - row.budget) / Math.abs(row.budget) * 100).toFixed(1)
                  : row.isPercent 
                    ? (row.actual - row.budget).toFixed(1)
                    : '0.0';
                const pctVarYoY = row.prior !== 0 && !row.isPercent
                  ? ((row.actual - row.prior) / Math.abs(row.prior) * 100).toFixed(1)
                  : row.isPercent 
                    ? (row.actual - row.prior).toFixed(1)
                    : '0.0';
                
                const isSubtotal = row.type === 'subtotal';
                const isHighlight = row.type === 'highlight';
                const isMargin = row.type === 'margin';
                
                // Color logic for variance (costs should be inverse)
                const budgetVarIsGood = row.isNegative 
                  ? parseFloat(pctVarVsBudget) <= 0 
                  : parseFloat(pctVarVsBudget) >= 0;
                const yoyVarIsGood = row.isNegative 
                  ? parseFloat(pctVarYoY) <= 0 
                  : parseFloat(pctVarYoY) >= 0;
                
                return (
                  <tr 
                    key={idx} 
                    className={`
                      border-b border-slate-100 
                      ${isSubtotal ? 'bg-slate-50 font-semibold' : ''} 
                      ${isHighlight ? 'bg-slate-100 font-bold' : ''} 
                      ${isMargin ? 'bg-white' : ''} 
                      hover:bg-slate-50/70 transition-colors
                    `}
                  >
                    <td className={`py-2.5 px-4 ${isMargin ? 'italic text-slate-500' : ''} ${row.type === 'row' ? 'pl-6' : ''}`}>
                      {row.label[language]}
                    </td>
                    <td className={`py-2.5 px-3 text-right tabular-nums ${isHighlight ? 'text-primary' : ''} ${isMargin ? 'italic text-slate-500' : ''}`}>
                      {row.isPercent ? `${row.actual.toFixed(1)}%` : formatM(row.actual)}
                    </td>
                    <td className={`py-2.5 px-3 text-right tabular-nums text-slate-500 ${isMargin ? 'italic' : ''}`}>
                      {row.isPercent ? `${row.budget.toFixed(1)}%` : formatM(row.budget)}
                    </td>
                    <td className={`py-2.5 px-3 text-right tabular-nums ${isMargin ? 'italic text-slate-400' : ''}`}>
                      {!isMargin ? (
                        <span style={{ color: budgetVarIsGood ? colors.status.positive : colors.status.negative }}>
                          {parseFloat(absVarVsBudget) > 0 ? '+' : ''}{absVarVsBudget}
                        </span>
                      ) : (
                        <span className="text-slate-400 italic">
                          {parseFloat(absVarVsBudget) > 0 ? '+' : ''}{absVarVsBudget} p.p.
                        </span>
                      )}
                    </td>
                    <td className="py-2.5 px-3 text-right">
                      {!isMargin ? (
                        <span 
                          className="inline-flex px-1.5 py-0.5 rounded text-xs font-semibold"
                          style={{ 
                            backgroundColor: budgetVarIsGood ? colors.status.positiveBg : colors.status.negativeBg,
                            color: budgetVarIsGood ? colors.status.positive : colors.status.negative
                          }}
                        >
                          {parseFloat(pctVarVsBudget) > 0 ? '+' : ''}{pctVarVsBudget}%
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400 italic">—</span>
                      )}
                    </td>
                    <td className={`py-2.5 px-3 text-right tabular-nums text-slate-400 ${isMargin ? 'italic' : ''}`}>
                      {row.isPercent ? `${row.prior.toFixed(1)}%` : formatM(row.prior)}
                    </td>
                    <td className="py-2.5 px-3 text-right">
                      {!isMargin ? (
                        <span 
                          className="text-xs font-medium"
                          style={{ color: yoyVarIsGood ? colors.status.positive : colors.status.negative }}
                        >
                          {parseFloat(pctVarYoY) > 0 ? '+' : ''}{pctVarYoY}%
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400 italic">
                          {parseFloat(pctVarYoY) > 0 ? '+' : ''}{pctVarYoY} p.p.
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default NetIncomeDetail;
